import { resetMockData } from "@a/admin";
import { confirmDialog } from "@c/modals/ConfirmDialog";
import { useDemoMode } from "@ctx/DemoModeContext";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@u/cn";
import { RotateCcw } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

/**
 * Acción demo del header del dashboard: reconstruye el contenido de la cuenta
 * mock desde las fuentes vivas (misma RPC que el botón admin de UserDetailPage;
 * la RPC también acepta al llamador is_demo).
 *
 * El viejo switcher de plan murió con el feature-gating (2026-09-16): el plan es
 * del usuario y sólo define tours/storage, marca y topes — no hay nada que
 * simular por tour.
 */
export default function DemoResetChip() {
  const { isDemoMode } = useDemoMode();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);

  if (!isDemoMode) return null;

  const handleReset = async () => {
    const ok = await confirmDialog({
      variant: "warning",
      title: t("admin_ud_reset_demo_title"),
      description: t("admin_ud_reset_demo_desc_confirm"),
      confirmLabel: t("admin_ud_reset_demo_confirm"),
      onConfirm: async () => {
        setPending(true);
        try {
          await resetMockData();
        } finally {
          setPending(false);
        }
      },
    });
    if (!ok) return;
    await queryClient.invalidateQueries();
    toast.success(t("admin_ud_reset_demo_success"));
  };

  return (
    <button
      onClick={handleReset}
      disabled={pending}
      aria-label={t("admin_ud_reset_demo_title")}
      title={t("admin_ud_reset_demo_title")}
      className="h-9 w-9 rounded-lg inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
    >
      <RotateCcw size={14} className={cn(pending && "animate-spin")} />
    </button>
  );
}
